import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getOrgUser, updateOrgUser } from "../services/organizationService";
import { OrganizationUser } from "../types/organization";
import { selectOrganization } from "../store/slices/authUserSlice";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const TeamMemberDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const organization = useSelector(selectOrganization);
  const hasInterviews = !!organization?.feature_flags?.content_interviews;

  const [user, setUser] = useState<OrganizationUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (id) {
      getOrgUser(id).then(setUser);
    }
  }, [id]);

  const handleUpdate = (updates: Partial<OrganizationUser>) => {
    if (!id) return;
    setSaving(true);
    updateOrgUser(id, updates)
      .then((updated) => {
        setUser(updated);
        setSuccessMessage("Saved");
        setTimeout(() => setSuccessMessage(""), 1500);
      })
      .catch(() => {
        setErrorMessage("Failed to save");
        setTimeout(() => setErrorMessage(""), 1500);
      })
      .finally(() => setSaving(false));
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div>
      <button
        onClick={() => navigate("/team")}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: "14px",
          color: "#6B7280",
          padding: 0,
          marginBottom: "16px",
        }}
      >
        &larr; Back to Team
      </button>

      <h1>{user.name || user.email}</h1>
      {user.name && (
        <p style={{ color: "#6B7280", margin: "-8px 0 24px" }}>{user.email}</p>
      )}

      {successMessage && (
        <div
          style={{
            backgroundColor: "#ECFDF5",
            color: "#065F46",
            padding: "10px",
            marginBottom: "15px",
            borderRadius: "4px",
            border: "1px solid #A7F3D0",
          }}
        >
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div
          style={{
            backgroundColor: "#FFEBEE",
            color: "#D32F2F",
            padding: "10px",
            marginBottom: "15px",
            borderRadius: "4px",
            border: "1px solid #FFCDD2",
          }}
        >
          {errorMessage}
        </div>
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          maxWidth: "500px",
        }}
      >
        {/* Status */}
        <div>
          <h3 style={{ margin: "0 0 8px" }}>Status</h3>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            <input
              type="checkbox"
              checked={user.active ?? true}
              onChange={() => handleUpdate({ active: !(user.active ?? true) })}
              disabled={saving}
              style={{ cursor: "pointer" }}
            />
            Active
          </label>
        </div>

        {/* Email Preferences */}
        <div>
          <h3 style={{ margin: "0 0 8px" }}>Email Preferences</h3>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              <input
                type="checkbox"
                checked={user.receive_content_plan_emails ?? true}
                onChange={() =>
                  handleUpdate({
                    receive_content_plan_emails: !(
                      user.receive_content_plan_emails ?? true
                    ),
                  })
                }
                disabled={saving}
                style={{ cursor: "pointer" }}
              />
              Content Updates
            </label>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              <input
                type="checkbox"
                checked={user.receive_approval_emails ?? true}
                onChange={() =>
                  handleUpdate({
                    receive_approval_emails: !(
                      user.receive_approval_emails ?? true
                    ),
                  })
                }
                disabled={saving}
                style={{ cursor: "pointer" }}
              />
              Reminders & Approvals
            </label>
          </div>
        </div>

        {/* Interview Emails - gated by feature flag */}
        {hasInterviews && (
          <div>
            <h3 style={{ margin: "0 0 8px" }}>Interview Emails</h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                }}
              >
                Frequency:
                <select
                  value={user.content_interview_email_frequency ?? "weekly"}
                  onChange={(e) =>
                    handleUpdate({
                      content_interview_email_frequency: e.target.value,
                    })
                  }
                  disabled={saving}
                  style={{
                    padding: "6px 10px",
                    borderRadius: "6px",
                    border: "1px solid #D1D5DB",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  <option value="none">None</option>
                  <option value="daily">Daily</option>
                  <option value="every_other_day">Every Other Day</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </label>

              {user.content_interview_email_frequency !== "none" &&
                user.content_interview_email_frequency !== "daily" &&
                user.content_interview_email_frequency !== "every_other_day" && (
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                    }}
                  >
                    {user.content_interview_email_frequency === "monthly"
                      ? "Day of month:"
                      : "Day of week:"}
                    {user.content_interview_email_frequency === "monthly" ? (
                      <select
                        value={user.content_interview_email_day ?? 1}
                        onChange={(e) =>
                          handleUpdate({
                            content_interview_email_day: parseInt(
                              e.target.value
                            ),
                          })
                        }
                        disabled={saving}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #D1D5DB",
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(
                          (day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          )
                        )}
                      </select>
                    ) : (
                      <select
                        value={user.content_interview_email_day ?? 1}
                        onChange={(e) =>
                          handleUpdate({
                            content_interview_email_day: parseInt(
                              e.target.value
                            ),
                          })
                        }
                        disabled={saving}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "6px",
                          border: "1px solid #D1D5DB",
                          fontSize: "14px",
                          cursor: "pointer",
                        }}
                      >
                        {DAYS_OF_WEEK.map((day) => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamMemberDetail;
